import { api } from '../lib/api';
import type { AdHocCharge, BillingCadence, BillingConfig, ChargeType, Invoice, InvoiceFrequency, PendingChargesPreview } from '../types/api';

export async function getBillingConfig(orgId: string): Promise<BillingConfig> {
  const { data } = await api.get<BillingConfig>(`/customer-organisations/${orgId}/billing-config`);
  return data;
}

export interface UpdateBillingConfigInput {
  defaultChargeType?: ChargeType;
  vatRate?: number;
  billingCadence?: BillingCadence;
  billingDayOfMonth?: number;
  billingDayOfWeek?: number;
  defaultManagedTermMonths?: number;
  defaultManagedFrequency?: InvoiceFrequency;
}

export async function updateBillingConfig(orgId: string, input: UpdateBillingConfigInput): Promise<BillingConfig> {
  const { data } = await api.patch<BillingConfig>(`/customer-organisations/${orgId}/billing-config`, input);
  return data;
}

export async function previewPendingCharges(orgId: string): Promise<PendingChargesPreview> {
  const { data } = await api.get<PendingChargesPreview>(`/customer-organisations/${orgId}/pending-charges`);
  return data;
}

export async function listInvoices(customerOrganisationId?: string): Promise<Invoice[]> {
  const params = customerOrganisationId ? `?customerOrganisationId=${customerOrganisationId}` : '';
  const { data } = await api.get<Invoice[]>(`/invoices${params}`);
  return data;
}

export async function getInvoice(id: string): Promise<Invoice> {
  const { data } = await api.get<Invoice>(`/invoices/${id}`);
  return data;
}

export interface RunInvoicingResult {
  invoice?: Invoice | null;
  skippedReason?: string | null;
}

// Omit customerOrganisationId to run the daily due-check on demand (bills
// every customer whose nextBillingDate has arrived); provide it to
// force-bill one customer immediately regardless of due date.
export async function runInvoicing(customerOrganisationId?: string): Promise<RunInvoicingResult | RunInvoicingResult[]> {
  const { data } = await api.post('/invoicing/run', { customerOrganisationId });
  return data;
}

// §113: ad-hoc managed contracts - a flat charge spread over a term, not
// tied to any shop order/catalog item.
export async function listAdHocCharges(orgId: string): Promise<AdHocCharge[]> {
  const { data } = await api.get<AdHocCharge[]>(`/customer-organisations/${orgId}/ad-hoc-charges`);
  return data;
}

export interface CreateAdHocChargeInput {
  siteId: string;
  description: string;
  totalAmount: number;
  costAmount?: number;
  termMonths: number;
  frequency: InvoiceFrequency;
}

export async function createAdHocCharge(orgId: string, input: CreateAdHocChargeInput): Promise<AdHocCharge> {
  const { data } = await api.post<AdHocCharge>(`/customer-organisations/${orgId}/ad-hoc-charges`, input);
  return data;
}

export async function cancelAdHocCharge(id: string): Promise<AdHocCharge> {
  const { data } = await api.patch<AdHocCharge>(`/ad-hoc-charges/${id}/cancel`, {});
  return data;
}
